import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";

interface NewsletterPreviewProps {
  searchText: string;
  emailSubject?: string;
  emailBody: string[];
  senderName?: string;
  senderEmail?: string;
  avatarLetter?: string;
}

const NewsletterPreview = ({
  searchText,
  emailBody,
  emailSubject = "Guest's Newsletter",
  senderName = "Guest's Newsletter",
  senderEmail = "guest@ainews.email",
  avatarLetter = "G",
}: NewsletterPreviewProps) => {
  return (
    <div className="flex items-center justify-start p-2 sm:p-4">
      <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-xl xl:max-w-2xl 2xl:max-w-3xl">
        <Card className="w-full bg-white rounded-2xl sm:rounded-3xl shadow-lg p-4 sm:p-6 md:p-8 relative overflow-hidden max-h-[350px] sm:max-h-[450px] md:max-h-[500px] lg:max-h-[600px]">
          {/* Quote Bubble */}
          <div className="search-shimmer relative bg-gradient-to-r from-blue-50 to-purple-50 rounded-full p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 overflow-hidden">
            <p className="sm:px-2text-gray-800 font-medium relative z-10 text-sm sm:text-base text-start">
              "{searchText}"
            </p>

            {/* Search shimmer overlay */}
            <div className="absolute inset-0 rounded-full search-shimmer pointer-events-none z-20"></div>
          </div>
          <hr className="my-4 sm:my-6" />
          {/* Email Subject */}
          <h1 className=" text-start text-sm md:text-xl sm:text-lg font-bold text-gray-800 mb-6">
            {emailSubject}
          </h1>

          {/* Sender Information */}
          <div className="flex text-start space-x-2 sm:space-x-3 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm sm:text-lg">
                {avatarLetter}
              </span>
            </div>
            <div>
              <h3 className="text-gray-700 font-semibold text-start text-sm sm:text-base">
                {senderName}
              </h3>
              <p className="text-gray-600 text-start text-xs sm:text-sm">
                {senderEmail}
              </p>
            </div>
          </div>

          {/* Email Content */}
          <div className="space-y-3 sm:space-y-4 pb-4">
            {emailBody.map((content, index) => {
              // Check if content contains newlines (paragraphs) or is a single line (bullet point)
              const isParagraph = content.includes("\n");

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className={
                    isParagraph ? "" : "flex items-start space-x-2 sm:space-x-3"
                  }
                >
                  {!isParagraph && (
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 text-start bg-gray-800 rounded-full mt-2 flex-shrink-0"></div>
                  )}
                  <div className="text-gray-800 leading-relaxed text-start text-sm sm:text-base">
                    {isParagraph ? (
                      <pre className="whitespace-pre-wrap font-sans">
                        {content}
                      </pre>
                    ) : (
                      <p>{content}</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Fade gradient overlay - more visible */}
          <div className="absolute bottom-0 left-0 right-0 h-12 sm:h-16 md:h-20 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none rounded-b-2xl sm:rounded-b-3xl"></div>
        </Card>
      </div>
    </div>
  );
};

export default NewsletterPreview;
